import { ActorReference } from '@/features/references/ActorReference';
import { TaskDependencies } from '@/features/task/TaskDependencies';
// src/pages/task/TaskDetailPage.tsx

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useTask, useI18n } from '@react/hooks';
import { entityStore } from '@/stores/EntityStore';
import { useTabId } from '@/components/tabs/TabContext';
import {
  Button,
  Alert,
  Badge,
  ErrorAlert,
} from '@react/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  buildTaskFieldUpdate,
  taskPriorityMap,
  taskStatusMap,
  getStatusMapping,
  formatDateTime,
  formatDuration,
  formatEnumLabel,
  getDueDateUrgency,
  getPriorityIconPath,
  getPriorityColor,
  getTaskStatus,
  getTaskPriority,
  getTaskAssignee,
  getTaskDueDate,
  getTaskCustomStatus,
  getTaskWatchers,
  getTaskTags,
  getTaskKey,
  getTaskLabels,
  getTaskStartDate,
  getTaskCompletedAt,
  getTaskProgress,
  getTaskEstimatedDuration,
  getTaskActualDuration,
  getTaskScheduledWork,
  getTaskCalendar,
  type BadgeVariant,
} from '@/features/task/logic';
import { PropertyItem } from '@/features/task/detail/PropertyList';
import { ParentLink } from '@/features/task/detail/ParentLink';
import { AnchoredMenu, MenuItem } from '@/components/AnchoredMenu';
import { TaskStatusControl } from '@/features/task/TaskStatusControl';
import { TaskAssigneeControl } from '@/features/task/TaskAssigneeControl';
import { TaskWorkPlanning } from '@/features/task/TaskWorkPlanning';
import { TaskCustomFieldsPanel } from '@/features/task/TaskCustomFieldsPanel';
import { TaskWatchers } from '@/features/task/TaskWatchers';
import { TaskSubtasks } from '@/features/task/TaskSubtasks';
import { TaskActivityFeed } from '@/features/task/TaskActivityFeed';
import { useTaskDetail } from '@/features/task/useTaskDetail';
import { AgentRunPanel } from '@/features/task/AgentRunPanel';
import type { Task } from '@core/types/task';
import { entityToUrn } from '@/features/resources/logic';

// --- Inline SVG icons ---

function EditIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
  );
}

function MoreIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
    </svg>
  );
}

function mapPriorityToVariant(priority: string): BadgeVariant {
  return getStatusMapping(taskPriorityMap, priority).variant;
}

function mapStatusToVariant(status: string): BadgeVariant {
  return getStatusMapping(taskStatusMap, status).variant;
}

export const TaskDetailPage = observer(() => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const store = useTask();
  const { t } = useI18n();
  const tabId = useTabId();
  const detail = useTaskDetail(id);
  const item = detail.task as any;
  const [actionError, setActionError] = useState<unknown>(null);

  const [showActions, setShowActions] = useState(false);
  useEffect(() => { setActionError(null); }, [detail.key]);

  const refresh = () => { void detail.reload(); };

  const handleSaveDescription = async (description: string) => {
    if (!id) return;
    try {
      await store.UpdateTask(id, buildTaskFieldUpdate(id, { description }));
      refresh();
    } catch (error) { setActionError(error); throw error; }
  };

  useEffect(() => () => {
    if (entityStore.stateFor(tabId).currentEntity?.id === id) entityStore.clearContext(tabId);
  }, [id, tabId, detail.key]);

  // Load entity context for Right Rail.
  // Task responses carry no `urn` field — compose it from the entity's parts.
  const taskUrn = item?.entity?.urn || (item?.entity ? entityToUrn(item.entity) : '');
  useEffect(() => {
    if (item?.entity?.id === id) {
      entityStore.loadEntityData(tabId, {
        urn: taskUrn,
        type: item.entity.type_name || 'task',
        id: item.entity.id || id!,
        title: item.title || '',
      });
      if (item.aspects?.items) {
        entityStore.setAspects(tabId, item.aspects.items);
      }
    }
  }, [item?.entity?.id, item?.title, taskUrn, id, tabId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDelete = async () => {
    if (!id || !confirm(t('task.deleteConfirm'))) return;
    try {
      await store.DeleteTask(id);
      navigate('/tasks');
    } catch (error) { setActionError(error); }
  };

  // Only this page's read controls its loading state; panels and actions
  // keep their own request state. A different tab cannot replace this task.
  if (detail.isLoading && !item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse space-y-4 w-full max-w-2xl">
          <div className="h-8 bg-muted rounded w-1/3" />
          <div className="h-4 bg-muted rounded w-1/2" />
          <div className="h-64 bg-muted rounded" />
        </div>
      </div>
    );
  }

  // Error
  if (detail.error && !item) {
    return (
      <>
        <ErrorAlert error={detail.error} />
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </>
    );
  }

  // Not found
  if (!item) {
    return (
      <Alert variant="warning" title={t('common.notFound')}>
        <Link to="/tasks" className="mt-2 inline-block text-sm underline text-primary">
          {t('common.backToList')}
        </Link>
      </Alert>
    );
  }

  // GetTask returns fields flat on the task object; list rows nest them under
  // `task`. The accessors in features/task/logic read both — never read
  // `item.task.*` directly, that shape is absent here.
  const status = getTaskStatus(item);
  const priority = getTaskPriority(item);
  const assignee = getTaskAssignee(item);
  const dueDate = getTaskDueDate(item);
  const dueDateUrgency = getDueDateUrgency(dueDate, status);
  const priorityIconPath = getPriorityIconPath(priority);
  const priorityColorClass = getPriorityColor(priority);

  return (
    <TaskDetailBody
      id={id}
      item={item}
      status={status}
      priority={priority}
      assignee={assignee}
      dueDate={dueDate}
      dueDateUrgency={dueDateUrgency}
      priorityIconPath={priorityIconPath}
      priorityColorClass={priorityColorClass}
      refresh={refresh}
      onDelete={handleDelete}
      onSaveDescription={handleSaveDescription}
      showActions={showActions}
      setShowActions={setShowActions}
      storeError={actionError || detail.error}
      clearError={() => { setActionError(null); detail.clearError(); }}
    />
  );
});

/**
 * Loose records, deliberately: the detail loader reads a task through the
 * generated DTO where nested aspects arrive as open maps, and narrowing them
 * here would only move the casts one file over.
 */
type TaskRecord = Record<string, any>;

interface TaskDetailBodyProps {
  id?: string;
  item: TaskRecord;
  status: string;
  priority: string;
  assignee: TaskRecord | undefined;
  dueDate: string | undefined;
  dueDateUrgency: string;
  priorityIconPath?: string;
  priorityColorClass: string;
  refresh: () => void;
  onDelete: () => void;
  onSaveDescription: (description: string) => Promise<void>;
  showActions: boolean;
  setShowActions: (v: boolean) => void;
  storeError: unknown;
  clearError: () => void;
}

/**
 * One labelled group inside the Details panel. Groups are separated by a
 * hairline, not by cards: the panel is one surface, read top to bottom —
 * Status → People → Time → Meta.
 */
function DetailGroup({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="border-t border-border px-3 py-3 first:border-t-0 first:pt-2">
      <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      {children}
    </div>
  );
}

/**
 * The page itself. Split out from the loader so the loader can gate on the
 * fetch without unmounting the body.
 *
 * A task reads as a document of work: one content column (description,
 * subtasks, a quieter activity log) and one Details panel on the right that
 * groups every property and control under four labels. No per-widget cards.
 */
const TaskDetailBody = observer(({
  id,
  item,
  status,
  priority,
  assignee,
  dueDate,
  dueDateUrgency,
  priorityIconPath,
  priorityColorClass,
  refresh,
  onDelete,
  onSaveDescription,
  showActions,
  setShowActions,
  storeError,
  clearError,
}: TaskDetailBodyProps) => {
  const actionsTriggerRef = useRef<HTMLButtonElement>(null);
  const { t } = useI18n();
  const navigate = useNavigate();
  const parentType = item?.parent?.type_name?.replace(/^core\./, '');
  const hasProject = parentType === 'project' || !!item?.project_context?.project_refs?.some((ref: any) => ref.project?.id);
  const peopleScope = id && hasProject
    ? { typeName: 'task', id }
    : parentType === 'task' && item.parent?.id
      ? { typeName: 'task', id: item.parent.id }
      : item?.entity?.platform_id
        ? { typeName: 'platform', id: item.entity.platform_id }
        : undefined;

  const watchers = getTaskWatchers(item);
  const tags: string[] = getTaskTags(item);
  const labels: TaskRecord[] = getTaskLabels(item);
  const customStatus = getTaskCustomStatus(item);
  const statusLabel = customStatus || (status ? formatEnumLabel(status, 'TASK_STATUS_') : '');

  // Inline description editing: click the prose (or the empty-state link) and
  // it becomes a textarea with the raw markdown. ⌘⏎ saves, Escape cancels.
  const [activityRefresh, setActivityRefresh] = useState(0);
  const [editingDescription, setEditingDescription] = useState(false);
  const [descriptionDraft, setDescriptionDraft] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editingDescription) descriptionInputRef.current?.focus();
  }, [editingDescription]);

  const startEditDescription = () => {
    setDescriptionDraft(String(item.description ?? ''));
    setEditingDescription(true);
  };

  const saveDescription = async () => {
    const description = descriptionDraft.trim();
    setSavingDescription(true);
    try {
      await onSaveDescription(description);
      setEditingDescription(false);
    } catch { /* store surfaces the error */ } finally {
      setSavingDescription(false);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl">
      {/* Wayfinding lives in the workspace chrome and the tab strip; the page
          starts at the title. The parent, when there is one, is a chip on the
          meta line below. */}
      {storeError ? (
        <div className="mb-4">
          <ErrorAlert error={storeError} dismissible onDismiss={clearError} />
        </div>
      ) : null}

      {/* Title row */}
      <div className="flex items-start gap-3">
        <h1 className="min-w-0 flex-1 text-2xl font-semibold text-foreground">
          {String(item.title ?? '')}
        </h1>
        <div className="flex shrink-0 items-center gap-1.5">
          <Link to={`/tasks/${id}/edit`}>
            <Button variant="ghost" size="sm">
              <EditIcon className="mr-1.5 h-4 w-4" />
              {t('common.edit')}
            </Button>
          </Link>
          <div className="relative">
            <Button ref={actionsTriggerRef} variant="ghost" size="sm" onClick={() => setShowActions(!showActions)}>
              <MoreIcon className="h-4 w-4" />
            </Button>
            <AnchoredMenu
              anchor={actionsTriggerRef}
              open={showActions}
              onClose={() => setShowActions(false)}
              align="end"
              ariaLabel={t('common.actions')}
              className="w-48 py-1"
            >
              <MenuItem
                label={t('task.openInBoard')}
                onSelect={() => { navigate(`/tasks?view=board&task=${id}`); setShowActions(false); }}
              />
              <MenuItem
                label={t('task.automations')}
                onSelect={() => { navigate('/workflows'); setShowActions(false); }}
              />
              <MenuItem
                label={t('common.delete')}
                destructive
                onSelect={() => { onDelete(); setShowActions(false); }}
              />
            </AnchoredMenu>
          </div>
        </div>
      </div>

      {/* One quiet meta line: the status badge is the single accent. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-muted-foreground">
        <span className="font-mono tabular-nums">{getTaskKey(item) || t('task.unnumbered')}</span>
        {statusLabel && (
          <Badge variant={mapStatusToVariant(status)} size="sm">{statusLabel}</Badge>
        )}
        {item.parent && (
          <span className="inline-flex items-center rounded bg-muted px-1.5 py-0.5">
            <ParentLink parent={item.parent} />
          </span>
        )}
        <span className="tabular-nums">{formatDateTime(item.created_at)}</span>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_20rem]">
        {/* Content column — the document itself */}
        <div className="min-w-0">
          {editingDescription ? (
            <div className="space-y-2">
              <textarea
                ref={descriptionInputRef}
                rows={Math.min(16, Math.max(4, descriptionDraft.split('\n').length + 1))}
                aria-label={t('task.field.description')}
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setEditingDescription(false);
                  if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') void saveDescription();
                }}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <div className="flex gap-1.5">
                <Button size="sm" onClick={() => void saveDescription()} isLoading={savingDescription}>
                  {t('common.save')}
                </Button>
                <Button size="sm" variant="ghost" disabled={savingDescription} onClick={() => setEditingDescription(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>
          ) : item.description ? (
            <div
              role="button"
              tabIndex={0}
              onClick={startEditDescription}
              onKeyDown={(e) => { if (e.key === 'Enter') startEditDescription(); }}
              className="-m-1 cursor-text rounded-md p-1 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <div className="prose prose-sm max-w-none text-foreground dark:prose-invert">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {String(item.description)}
                </ReactMarkdown>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={startEditDescription}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('task.addDescription')}
            </button>
          )}

          {id && <AgentRunPanel taskId={id} task={item as Task} onChanged={refresh} onEditSpec={startEditDescription} />}

          {/* Everything you do to the task's parts (checklists, time,
              dependencies, attachments) lives in the right rail's Work tab. */}
          <div className="mt-8 space-y-8">
            {id && <TaskSubtasks parentId={id} parentTypeName={item.entity?.type_name || 'task'} onChanged={() => setActivityRefresh((value) => value + 1)} />}

            {id && <TaskDependencies taskId={id} />}

            {id && <TaskActivityFeed taskId={id} compact refreshKey={activityRefresh} />}
          </div>
        </div>

        {/* Details — one panel, four groups */}
        <div className="rounded-lg border border-border bg-card">
          <p className="px-3 pb-1 pt-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">
            {t('task.details')}
          </p>

          <DetailGroup label={t('task.field.status')}>
            {id && (
              <TaskStatusControl
                taskId={id}
                currentStatus={status}
                currentCustomStatus={customStatus}
                onChanged={refresh}
                bare
              />
            )}
            <dl className="mt-3 divide-y divide-border/60">
              <PropertyItem label={t('task.field.priority')}>
                {priority ? (
                  <>
                    {priorityIconPath && (
                      <svg className={`h-4 w-4 ${priorityColorClass}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                        <path d={priorityIconPath} />
                      </svg>
                    )}
                    <Badge variant={mapPriorityToVariant(priority)} size="sm">
                      {formatEnumLabel(priority, 'TASK_PRIORITY_')}
                    </Badge>
                  </>
                ) : null}
              </PropertyItem>
            </dl>
          </DetailGroup>

          <DetailGroup label={t('task.people')}>
            <dl className="divide-y divide-border/60">
              <PropertyItem label={t('task.field.assignee')} stacked>
                {id ? (
                  <TaskAssigneeControl taskId={id} peopleScope={peopleScope} assignee={assignee} onChanged={refresh} bare />
                ) : null}
              </PropertyItem>
              <PropertyItem label={t('task.creator')}>
                <ActorReference actor={item.creator} />
              </PropertyItem>
            </dl>
            {id && (
              <div className="mt-3">
                <TaskWatchers taskId={id} peopleScope={peopleScope} watchers={watchers} onChanged={refresh} bare />
              </div>
            )}
          </DetailGroup>

          <DetailGroup label={t('task.time')}>
            <dl className="divide-y divide-border/60">
              <PropertyItem label={t('task.field.dueDate')}>
                {dueDate ? (
                  <>
                    <span className={`tabular-nums ${
                      dueDateUrgency === 'overdue' ? 'text-destructive' :
                      dueDateUrgency === 'soon' ? 'text-warning font-medium' : ''
                    }`}>
                      {formatDateTime(dueDate)}
                    </span>
                    {dueDateUrgency === 'overdue' && (
                      <Badge variant="error" size="sm">{t('task.overdue')}</Badge>
                    )}
                  </>
                ) : null}
              </PropertyItem>

              {getTaskStartDate(item) && (
                <PropertyItem label={t('task.startDate')}>
                  <span className="tabular-nums">{formatDateTime(getTaskStartDate(item))}</span>
                </PropertyItem>
              )}
              {getTaskCompletedAt(item) && (
                <PropertyItem label={t('task.completedAt')}>
                  <span className="tabular-nums">{formatDateTime(getTaskCompletedAt(item))}</span>
                </PropertyItem>
              )}

              {/* Progress earns its bar only once there is progress to show. */}
              {getTaskProgress(item) > 0 && (
                <PropertyItem label={t('task.progress')} stacked>
                  <div className="w-full">
                    <div className="mb-1 text-xs tabular-nums text-muted-foreground">{getTaskProgress(item)}%</div>
                    <div className="h-1.5 overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${Math.min(100, Math.max(0, getTaskProgress(item)))}%` }}
                      />
                    </div>
                  </div>
                </PropertyItem>
              )}

              {(getTaskEstimatedDuration(item) || getTaskActualDuration(item)) && (
                <PropertyItem label={t('task.duration')}>
                  <span className="tabular-nums">
                    {`${formatDuration(getTaskEstimatedDuration(item))} / ${formatDuration(getTaskActualDuration(item))}`}
                  </span>
                </PropertyItem>
              )}
            </dl>
            {id && (
              <div className="mt-3">
                <TaskWorkPlanning
                  taskId={id}
                  scheduledStart={getTaskScheduledWork(item)?.start_time || getTaskCalendar(item)?.start_time}
                  scheduledEnd={getTaskScheduledWork(item)?.end_time || getTaskCalendar(item)?.end_time}
                  onChanged={refresh}
                  bare
                />
              </div>
            )}
          </DetailGroup>

          <DetailGroup label={t('task.meta')}>
            <dl className="divide-y divide-border/60">
              <PropertyItem label={t('task.field.type')}>
                {item.type ? formatEnumLabel(String(item.type), 'TASK_TYPE_') : null}
              </PropertyItem>

              {item.parent && (
                <PropertyItem label={t('task.parent')}>
                  <ParentLink parent={item.parent} />
                </PropertyItem>
              )}

              {tags.length > 0 && (
                <PropertyItem label={t('task.field.tags')} stacked>
                  <div className="flex flex-wrap gap-1.5">
                    {tags.map((tag, idx) => (
                      <Badge key={`${tag}-${idx}`} variant="secondary" size="sm">{tag}</Badge>
                    ))}
                  </div>
                </PropertyItem>
              )}

              {labels.length > 0 && (
                <PropertyItem label={t('task.labels')} stacked>
                  <div className="flex flex-wrap gap-1.5">
                    {labels.map((label) => (
                      <span
                        key={label.id}
                        className="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium"
                        style={{
                          backgroundColor: label.color ? `${label.color}20` : undefined,
                          color: label.color || undefined,
                          border: `1px solid ${label.color || 'currentColor'}`,
                        }}
                      >
                        {label.name}
                      </span>
                    ))}
                  </div>
                </PropertyItem>
              )}

              {item.external_ref && (
                <PropertyItem label={t('task.externalRef')}>
                  {item.external_ref.external_url ? (
                    <a
                      href={item.external_ref.external_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-primary hover:underline"
                    >
                      {item.external_ref.system}: {item.external_ref.external_id}
                    </a>
                  ) : (
                    `${item.external_ref.system || ''}: ${item.external_ref.external_id || ''}`
                  )}
                </PropertyItem>
              )}

              {item.conversation?.id && (
                <PropertyItem label={t('task.conversation')}>
                  <Link to={`/chats/${item.conversation.id}`} className="text-primary hover:underline">
                    {t('task.conversationLink')}
                  </Link>
                </PropertyItem>
              )}

              <PropertyItem label={t('common.createdAt')}>
                <span className="tabular-nums">{formatDateTime(item.created_at)}</span>
              </PropertyItem>
              <PropertyItem label={t('common.updatedAt')}>
                <span className="tabular-nums">{formatDateTime(item.updated_at)}</span>
              </PropertyItem>
            </dl>
          </DetailGroup>

          {/* Custom fields keep their own group when the task has any. The
              panel returns null when none exist; :empty then hides the frame. */}
          {id && (
            <div className="border-t border-border px-3 py-3 [&:empty]:hidden">
              <TaskCustomFieldsPanel taskId={id} editable bare />
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default TaskDetailPage;
